import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form } from '@/components/ui/form';
import { toast } from '@/components/ui/use-toast';
import { FormFieldCheckBoxGroup } from '@/components/FormFieldCheckBoxGroup';
import { FormFieldSwitch } from '@/components/FormFieldSwitch';
import Loader from '@/components/Loader';
import { useRushPrioritySetting, useUpdateRushPrioritySetting } from '@/services/userSettingsService';
import { displayError } from '@/lib/error';
import {
  RushPrioritySettingFormValues,
  rushPrioritySettingSchema,
  WORK_ORDER_LIST_OPTIONS,
} from './settings.schema';

const ALL_LIST_VALUES = WORK_ORDER_LIST_OPTIONS.map((option) => option.value);

export const Settings: FC = () => {
  const settingQuery = useRushPrioritySetting();
  const updateMutation = useUpdateRushPrioritySetting();

  const form = useForm<RushPrioritySettingFormValues>({
    resolver: zodResolver(rushPrioritySettingSchema),
    defaultValues: { showRushOnTop: false, lists: [] },
  });
  const { watch, setValue, reset, handleSubmit } = form;
  const showRushOnTop = watch('showRushOnTop');
  const lists = watch('lists');
  const allSelected = lists.length === ALL_LIST_VALUES.length;

  useEffect(() => {
    if (settingQuery.data) reset(settingQuery.data);
  }, [settingQuery.data, reset]);

  const onSave = handleSubmit((values) => {
    updateMutation.mutate(values, {
      onSuccess: (saved) => {
        reset(saved);
        toast({ description: 'Settings saved successfully.', variant: 'default' });
      },
      onError: (error) => displayError(error),
    });
  });

  const renderContent = () => {
    if (settingQuery.isError) {
      return (
        <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed bg-white p-8 text-center">
          <div className="flex max-w-md flex-col items-center gap-4">
            <AlertTriangle className="h-10 w-10 text-error" />
            <p className="text-sm text-placeholder">Unable to load your settings.</p>
            <Button type="button" variant="outline" onClick={() => settingQuery.refetch()}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        </div>
      );
    }

    return (
      <Loader isLoading={settingQuery.isLoading} className="min-h-[200px]">
        <Form {...form}>
          <form onSubmit={onSave} className="flex flex-col gap-6 rounded-lg border bg-white p-6">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-medium text-black">Rush Work Orders</h2>
              <p className="text-sm text-placeholder">
                Show rush work orders ahead of other work orders in the lists you choose.
              </p>
            </div>

            <FormFieldSwitch name="showRushOnTop" label="Show Rush Work Orders on Top" />

            {showRushOnTop && (
              <div className="flex flex-col gap-3">
                <label className="flex w-fit cursor-pointer items-center space-x-2 text-sm">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(checked) =>
                      setValue('lists', checked === true ? [...ALL_LIST_VALUES] : [], {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                  />
                  <span className="font-medium">Select all</span>
                </label>
                <FormFieldCheckBoxGroup
                  name="lists"
                  label="Apply to"
                  required
                  options={WORK_ORDER_LIST_OPTIONS.map((option) => ({ ...option }))}
                />
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </Loader>
    );
  };

  return (
    <div className="flex w-full flex-col gap-5">
      <h1 className="text-3xl font-semibold text-black">Settings</h1>
      {renderContent()}
    </div>
  );
};
